/**
 * useSessionHeartbeat - Hook per mantenere attiva la sessione utente
 *
 * Questo hook invia periodicamente un "heartbeat" al backend per:
 * 1. Aggiornare LastActivityDate della sessione
 * 2. Permettere al backend di contare solo utenti realmente attivi
 * 3. Estendere la scadenza della sessione durante l'uso attivo
 *
 * @param {boolean} enabled - Se true, il heartbeat è attivo
 * @param {number} interval - Intervallo in millisecondi (default: 5 minuti)
 */

import { useEffect, useRef } from 'react';
import { protectedApi } from '../services/api';
import logger from '../utils/logger';

const HEARTBEAT_ENDPOINT = '/api/Auth/heartbeat';
const DEFAULT_INTERVAL = 5 * 60 * 1000; // 5 minuti

export const useSessionHeartbeat = (enabled = true, interval = DEFAULT_INTERVAL) => {
  const intervalRef = useRef(null);
  const lastHeartbeatRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      logger.log('[useSessionHeartbeat] Heartbeat disabilitato');
      return;
    }

    const sendHeartbeat = async () => {
      try {
        logger.log('[useSessionHeartbeat] Invio heartbeat...');
        const response = await protectedApi.post(HEARTBEAT_ENDPOINT);

        if (response.data?.success) {
          lastHeartbeatRef.current = new Date();
          logger.log('[useSessionHeartbeat] Heartbeat inviato con successo');
        } else {
          logger.warn('[useSessionHeartbeat] Heartbeat fallito:', response.data?.message);
        }
      } catch (error) {
        // Non logghiamo errori troppo verbosamente per non intasare i log
        // Se l'utente è offline o il token è scaduto, verrà gestito dal sistema di autenticazione
        if (error.response?.status !== 401) {
          logger.error('[useSessionHeartbeat] Errore heartbeat:', error.message);
        }
      }
    };

    // Invia heartbeat immediatamente
    sendHeartbeat();

    // Poi invia ogni N minuti
    intervalRef.current = setInterval(sendHeartbeat, interval);

    logger.log(`[useSessionHeartbeat] Heartbeat attivato (intervallo: ${interval / 1000}s)`);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        logger.log('[useSessionHeartbeat] Heartbeat disattivato');
      }
    };
  }, [enabled, interval]);

  return {
    lastHeartbeat: lastHeartbeatRef.current,
  };
};

export default useSessionHeartbeat;
